import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	ClientPortfolioDomainEnum,
	ClientPortfolioPaymentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { AccidentInsuranceNotice } from "web_myplann/Portfolio/Forms/NonLifeInsuranceForm/AccidentInsuranceNotice";
import type {
	NonLifeInsuranceFormInput,
	NonLifeInsuranceFormOutput,
} from "web_myplann/Portfolio/Forms/NonLifeInsuranceForm/nonLifeInsuranceFormSchema";
import type { NonLifeInsuranceType } from "web_myplann/Portfolio/Forms/NonLifeInsuranceForm/nonLifeInsuranceTypes";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type NonLifeInsuranceFormFieldsProps = {
	control: Control<
		NonLifeInsuranceFormInput,
		unknown,
		NonLifeInsuranceFormOutput
	>;
	insuranceType?: NonLifeInsuranceType;
};

export const NonLifeInsuranceFormFields = ({
	control,
	insuranceType,
}: NonLifeInsuranceFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const paymentFrequencyOptions = useEnumSelectOptions(
		ClientPortfolioPaymentFrequencyEnum,
		Resources.PortfolioDetail.SharedLabels.Frequency,
	);

	const {
		producerOptions,
		productOptions,
		isLoadingProducers,
		isLoadingProducts,
	} = useProducerProductOptions(
		control,
		ClientPortfolioDomainEnum.PropertyInsurance,
	);

	return (
		<>
			{insuranceType === "accident" && <AccidentInsuranceNotice />}

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddNonLifeInsurance.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddNonLifeInsurance.Fields.name)}
					placeholder={t(
						insuranceType
							? Resources.Portfolio.AddNonLifeInsurance.NamePlaceholders[
									insuranceType
								]
							: Resources.Portfolio.AddShared.Placeholders.fill,
					)}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(
						Resources.Portfolio.AddNonLifeInsurance.Fields.contractNumber,
					)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormCombobox
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddNonLifeInsurance.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
					isLoading={isLoadingProducers}
				/>
				<FlatRowFormCombobox
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddNonLifeInsurance.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
					isLast
				/>
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(
					t,
					Resources.Portfolio.AddNonLifeInsurance.Fields,
				)}
			/>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddNonLifeInsurance.Sections.insurance)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="insurance"
					type="float"
					label={t(Resources.Portfolio.AddNonLifeInsurance.Fields.insurance)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					suffix="Kč"
				/>
				<FlatRowFormInput
					control={control}
					name="insurancePeriod"
					type="float"
					label={t(
						Resources.Portfolio.AddNonLifeInsurance.Fields.insurancePeriod,
					)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
					suffix="Kč"
				/>
				<FlatRowFormSelect
					control={control}
					name="paymentFrequency"
					label={t(
						Resources.Portfolio.AddNonLifeInsurance.Fields.paymentFrequency,
					)}
					options={paymentFrequencyOptions}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddNonLifeInsurance.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddNonLifeInsurance.Fields.signedAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddNonLifeInsurance.Fields.startAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddNonLifeInsurance.Fields.endAt)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					isLast
				/>
			</Stack>
		</>
	);
};
